import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ShieldCheck, Loader2, Key } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface MFAGateProps {
  children: React.ReactNode;
}

/**
 * MFAGate wraps the app and shows a TOTP challenge dialog when MFA is required.
 * This handles both password and OAuth logins - the MFA check happens in AuthContext.
 */
const MFAGate = ({ children }: MFAGateProps) => {
  const { requiresMFA, completeMFAChallenge, cancelMFAChallenge } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');

  const handleCodeChange = (value: string) => {
    if (mode === 'totp') {
      setCode(value.replace(/\D/g, '').slice(0, 6));
    } else {
      setCode(value.toLowerCase().slice(0, 9));
    }
    setError(null);
  };

  const handleVerify = async () => {
    if (mode === 'totp' && code.length !== 6) return;
    if (mode === 'recovery' && code.length < 8) return;

    setIsLoading(true);
    setError(null);

    try {
      if (mode === 'totp') {
        const success = await completeMFAChallenge(code);
        if (success) {
          toast.success('Two-factor authentication verified');
          setCode('');
        } else {
          setError('Invalid code. Please try again.');
          setCode('');
        }
      } else {
        // Recovery code verification
        const { data, error: verifyError } = await supabase.functions.invoke('recovery-codes', {
          body: { action: 'verify', code },
        });

        if (verifyError || !data?.valid) {
          setError('Invalid recovery code. Please try again.');
          setCode('');
        } else {
          // Recovery code valid - complete MFA challenge with a special flow
          // We need to mark MFA as complete without TOTP
          toast.success('Recovery code accepted');
          // Sign out and sign back in to clear the AAL requirement
          // This is a workaround since recovery codes bypass TOTP
          window.location.reload();
        }
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    await cancelMFAChallenge();
    setCode('');
    setError(null);
    setMode('totp');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  // If MFA is not required, render children normally
  if (!requiresMFA) {
    return <>{children}</>;
  }

  // Show MFA challenge dialog
  return (
    <>
      {children}
      <Dialog open={requiresMFA} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-500" />
              Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              {mode === 'totp'
                ? 'Enter the 6-digit code from your authenticator app to continue.'
                : 'Enter one of your recovery codes to continue.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-danger-muted text-danger rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="mfa-code">
                {mode === 'totp' ? 'Verification Code' : 'Recovery Code'}
              </Label>
              <Input
                id="mfa-code"
                type="text"
                inputMode={mode === 'totp' ? 'numeric' : 'text'}
                pattern={mode === 'totp' ? '[0-9]*' : undefined}
                maxLength={mode === 'totp' ? 6 : 9}
                placeholder={mode === 'totp' ? '000000' : 'a3b7-c9d2'}
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`text-center text-2xl tracking-widest font-mono ${
                  mode === 'totp' ? '' : 'text-xl'
                }`}
                autoFocus
                disabled={isLoading}
              />
            </div>

            <div className="flex justify-between items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMode(mode === 'totp' ? 'recovery' : 'totp');
                  setCode('');
                  setError(null);
                }}
                disabled={isLoading}
              >
                <Key className="h-4 w-4 mr-2" />
                {mode === 'totp' ? 'Use Recovery Code' : 'Use Authenticator'}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel} className="flex-1" disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleVerify}
                disabled={
                  isLoading ||
                  (mode === 'totp' && code.length !== 6) ||
                  (mode === 'recovery' && code.length < 8)
                }
                className="flex-1"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MFAGate;
